import { useCallback, useEffect, useRef } from 'react';
import { throttle, debounce } from 'lodash';

interface UseInfiniteScrollProps {
    isLoading: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
}

export const useInfiniteScroll = ({
    isLoading,
    hasNextPage,
    onLoadMore
}: UseInfiniteScrollProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);

    const canLoadMore = useCallback(() => {
        return !isLoading && !loadingRef.current && hasNextPage;
    }, [isLoading, hasNextPage]);

    const loadMore = useCallback(() => {
        if (canLoadMore()) {
            loadingRef.current = true;
            onLoadMore();
            // Reset loading ref after a delay to prevent rapid requests
            setTimeout(() => {
                loadingRef.current = false;
            }, 1000);
        }
    }, [canLoadMore, onLoadMore]);

    const checkForMoreContent = useCallback(
        debounce(() => {
            if (!containerRef.current) return;
            
            const container = containerRef.current;
            const hasMoreSpace = container.scrollHeight <= container.clientHeight;
            
            if (hasMoreSpace) {
                loadMore();
            }
        }, 100),
        [loadMore]
    );

    const handleScroll = useCallback(
        throttle((e: Event) => {
            const target = e.target as HTMLDivElement;
            const threshold = 200; 
            const hasReachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
            
            if (hasReachedBottom) {
                loadMore();
            }
        }, 500), // Increased throttle time to prevent rapid requests
        [loadMore]
    );

    useEffect(() => {
        checkForMoreContent();
    }, [checkForMoreContent]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll]);

    return containerRef;
};

export default useInfiniteScroll;